import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import RichTextEditor from '../components/RichTextEditor';
import { useAuth } from '../context/AuthContext';
import * as releaseNoteService from '../services/releaseNoteService';
import type { ReleaseNote } from '../services/releaseNoteService';

function formatDateLong(dateStr: string) {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();

  // Add ordinal suffix
  let suffix = 'th';
  if (day === 1 || day === 21 || day === 31) suffix = 'st';
  else if (day === 2 || day === 22) suffix = 'nd';
  else if (day === 3 || day === 23) suffix = 'rd';

  return `${month} ${day}${suffix}, ${year}`;
}

export default function ReleaseNotes(): React.ReactElement {
  const { user, token } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_admin';

  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState<ReleaseNote | null>(null);
  const [formContent, setFormContent] = useState('');
  const [nextVersion, setNextVersion] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const selectedNote = releaseNotes.find(n => n.id === selectedId) || null;

  useEffect(() => {
    loadReleaseNotes();
  }, [token]);

  async function loadReleaseNotes() {
    if (!token) return;
    try {
      setLoading(true);
      const data = await releaseNoteService.getAllReleaseNotes(token);
      setReleaseNotes(data);
      if (data.length > 0 && !selectedId) {
        setSelectedId(data[0].id);
      }
    } catch (err: any) {
      setError('Failed to load release notes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function openCreateForm() {
    setEditingNote(null);
    setFormContent('');
    setNextVersion('');
    setShowForm(true);
    if (token) {
      try {
        const version = await releaseNoteService.getNextVersion(token);
        setNextVersion(version);
      } catch (err) {
        console.error('Failed to fetch next version:', err);
      }
    }
  }

  function openEditForm(note: ReleaseNote) {
    setEditingNote(note);
    setFormContent(note.content);
    setNextVersion('');
    setShowForm(true);
  }

  async function handleSave() {
    if (!token || !formContent.trim()) return;
    try {
      setSaving(true);
      if (editingNote) {
        const updated = await releaseNoteService.updateReleaseNote(editingNote.id, {
          content: formContent,
        }, token);
        setReleaseNotes(prev => prev.map(n => n.id === updated.id ? updated : n));
        setSelectedId(updated.id);
      } else {
        const created = await releaseNoteService.createReleaseNote({
          content: formContent,
        }, token);
        setReleaseNotes(prev => [created, ...prev]);
        setSelectedId(created.id);
      }
      setShowForm(false);
    } catch (err: any) {
      console.error('Failed to save release note:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!token || !deleteId) return;
    try {
      setDeleting(true);
      await releaseNoteService.deleteReleaseNote(deleteId, token);
      setReleaseNotes(prev => {
        const filtered = prev.filter(n => n.id !== deleteId);
        if (selectedId === deleteId) {
          setSelectedId(filtered.length > 0 ? filtered[0].id : null);
        }
        return filtered;
      });
      setDeleteId(null);
    } catch (err: any) {
      console.error('Failed to delete release note:', err);
    } finally {
      setDeleting(false);
    }
  }

  const todayFormatted = formatDateLong(new Date().toISOString());

  // Full-page create/edit form
  if (showForm) {
    return (
      <Layout>
        <div className="p-6">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {editingNote ? 'Edit Release Note' : 'Create Release Note'}
                </h1>
                <p className="text-indigo-100 mt-1">
                  {editingNote ? 'Update the release note content below' : 'Write the content for your new release note'}
                </p>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 space-y-6">
              {/* Read-only version and date */}
              {!editingNote && (
                <div className="flex items-center gap-8">
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Version</span>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {nextVersion || '...'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Date</span>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {todayFormatted}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <RichTextEditor
                  value={formContent}
                  onChange={setFormContent}
                  placeholder="Write your release notes here..."
                  minHeight="400px"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formContent.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : editingNote ? 'Update Release Note' : 'Create Release Note'}
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Release Notes</h1>
              <p className="text-indigo-100 mt-1">Stay up to date with the latest platform updates</p>
            </div>
            {isPlatformAdmin && (
              <button
                onClick={openCreateForm}
                className="px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors"
              >
                Create Release Note
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
            {error}
          </div>
        ) : releaseNotes.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No release notes yet</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {isPlatformAdmin ? 'Create the first release note to get started.' : 'Release notes will appear here when published.'}
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="flex">
              {/* Left sidebar: plain date list */}
              <div className="w-56 shrink-0 border-r border-gray-200 dark:border-gray-700 p-4">
                <div className="space-y-1">
                  {releaseNotes.map(note => (
                    <button
                      key={note.id}
                      onClick={() => setSelectedId(note.id)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors text-sm ${
                        selectedId === note.id
                          ? 'text-blue-600 dark:text-blue-400 font-semibold'
                          : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      {formatDateLong(note.published_at)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right content area */}
              <div className="flex-1 min-w-0 p-6">
                {selectedNote ? (
                  <div>
                    <div className="flex items-start justify-between mb-6">
                      <h2 className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                        {formatDateLong(selectedNote.published_at)}
                      </h2>
                      {isPlatformAdmin && (
                        <div className="flex items-center gap-2 shrink-0 ml-4">
                          <button
                            onClick={() => openEditForm(selectedNote)}
                            className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteId(selectedNote.id)}
                            className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <div
                      className="prose dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedNote.content }}
                    />
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">Select a release note to view</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteId(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete Release Note</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this release note? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
